import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../components/SeoHead";
import CleaningVideo from "../components/CleaningVideo";
import GeneralContactForm from "../components/GeneralContactForm";
import { BUSINESS_INFO, IMAGES, WHY_CHOOSE_US, ABOUT_OWNER } from "../data";

export default function Home() {
  return (
    <>
      <SeoHead
        title="Ses Multi Service"
        description="Ses Multi Service tilbyder grundig rengøring af sommerhuse og Airbnb i Norddjurs samt klip og vask af hunde under 10 kg. Kontakt os for en uforpligtende aftale."
        path="/"
      />

      {/* HERO SECTION */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#FAF7F2] via-[#F4EFE6] to-[#FAF7F2] py-8 sm:py-12 lg:py-14 border-b border-[#EADFCF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 lg:gap-12 items-stretch">
            
            {/* Hero Left Text Column */}
            <div className="lg:col-span-5 space-y-4 sm:space-y-5 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 bg-[#EADFCF] text-[#4B3C2D] px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide border border-[#D8C7B0] self-start">
                <i className="fa-solid fa-location-dot text-[#887055]" aria-hidden="true"></i>
                <span>Lokal rengøringsservice i Norddjurs</span>
              </div>

              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold font-heading text-[#2C2523] leading-[1.15]">
                Skinnende renhed i dit <span className="text-[#887055]">sommerhus & Airbnb</span>
              </h1>

              <p className="text-sm sm:text-base text-[#5C524C] leading-relaxed max-w-2xl">
                Hos <strong>Ses Multi Service</strong> leverer vi pålidelig, grundig og personlig rengøring af sommerhuse og ferieboliger i hele Norddjurs. Vi tjekker fejl og mangler, så din bolig altid fremstår indbydende.
              </p>

              {/* Service tags */}
              <div className="flex flex-wrap items-center gap-2 pt-0.5 text-xs font-medium text-[#6B5640]">
                <span className="bg-[#FFFDF9] border border-[#EADFCF] px-3 py-1.5 rounded-lg flex items-center gap-1.5 shadow-2xs">
                  <i className="fa-solid fa-house-chimney text-[#887055]" aria-hidden="true"></i> Sommerhusrengøring
                </span>
                <span className="bg-[#FFFDF9] border border-[#EADFCF] px-3 py-1.5 rounded-lg flex items-center gap-1.5 shadow-2xs">
                  <i className="fa-solid fa-key text-[#887055]" aria-hidden="true"></i> Airbnb-klargøring
                </span>
                <span className="bg-[#FFFDF9] border border-[#EADFCF] px-3 py-1.5 rounded-lg flex items-center gap-1.5 shadow-2xs">
                  <i className="fa-solid fa-dog text-[#887055]" aria-hidden="true"></i> Hundeklip (under 10 kg)
                </span>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                <Link
                  to="/kontakt"
                  className="bg-[#3A302B] hover:bg-[#231C19] text-[#FFFDF9] px-6 py-3 rounded-xl font-semibold text-sm transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer active:scale-[0.98]"
                >
                  <span>Kontakt Ses Multi Service</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>

                <a
                  href={`tel:${BUSINESS_INFO.phone}`}
                  className="bg-[#EADFCF] hover:bg-[#D8C7B0] text-[#2C2523] px-6 py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer border border-[#D8C7B0] whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-[#887055]" aria-hidden="true"></i>
                  <span className="whitespace-nowrap">Ring på {BUSINESS_INFO.phoneFormatted}</span>
                </a>
              </div>

              {/* Trust Badges */}
              <div className="pt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 border-t border-[#EADFCF]/80 text-xs text-[#5C524C]">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check-circle text-[#887055] text-sm" aria-hidden="true"></i>
                  <span>Aftørring & gulvvask</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check-circle text-[#887055] text-sm" aria-hidden="true"></i>
                  <span>Kontrol af skader</span>
                </div>
                <div className="flex items-center gap-2 col-span-2 sm:col-span-1">
                  <i className="fa-solid fa-check-circle text-[#887055] text-sm" aria-hidden="true"></i>
                  <span>Fotodokumentation</span>
                </div>
              </div>
            </div>

            {/* Hero Right Image Column (Full Height) */}
            <div className="lg:col-span-5 flex flex-col h-full min-h-[320px] sm:min-h-[400px] lg:min-h-[640px]">
              <div className="relative w-full h-full flex-1">
                {/* Image container in full height */}
                <div className="relative h-full w-full overflow-hidden rounded-2xl border border-[#D8C7B0] bg-white flex min-h-[320px] sm:min-h-[400px] lg:min-h-[640px]">
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    poster="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/161db869-3b85-456e-973a-3a2fd3e99bfc/imgi-21-761272825-122103779079292474-6780947939771872411-n.jpg"
                    className="w-full h-full min-h-[320px] sm:min-h-[400px] lg:min-h-[640px] object-cover"
                  >
                    <source src="https://assets.auxodev.dk/assets/161db869-3b85-456e-973a-3a2fd3e99bfc/7551100-uhd-3840-2160-25fps.mp4" type="video/mp4" />
                    Din browser understøtter ikke videoafspilning.
                  </video>
                  
                  {/* Subtle bottom gradient overlay */}
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 via-black/20 to-transparent pointer-events-none flex items-end p-4">
                    <p className="text-xs font-medium text-white/95 drop-shadow-sm flex items-center gap-1.5">
                      <i className="fa-solid fa-shield-halved text-[#D8C7B0]"></i>
                      Kvalitetsgaranti & grundig kontrol
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* PRIMARY SERVICES OVERVIEW - DESIGN REFERENCE CARD LAYOUT */}
      <section className="py-16 lg:py-24 bg-[#E8EFEA]/60 sm:bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header Section */}
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div className="max-w-2xl space-y-3">
              <span className="text-xs font-bold uppercase tracking-wider text-[#887055]">
                Vores tjenester
              </span>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-[#2C2523] tracking-tight">
                Vores hovedområder
              </h2>
              <p className="text-[#5C524C] text-base leading-relaxed">
                Vi tilbyder grundig og professionel rengøring af sommerhuse, Airbnb, private boliger og dødsbo i Norddjurs samt skånsom klip og vask af hunde under 10 kg. Se vores serviceområder herunder.
              </p>
            </div>
          </div>

          {/* Service Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 [&>*]:lg:col-span-2 [&>*:nth-child(4)]:lg:col-span-3 [&>*:nth-child(5)]:lg:col-span-3 [&>*:last-child:nth-child(2n+1)]:md:max-lg:col-span-2">

            {/* Card 1: Sommerhusrengøring */}
            <article className="relative h-[500px] rounded-3xl overflow-hidden group shadow-sm flex flex-col justify-end p-8 border border-[#EADFCF]">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/161db869-3b85-456e-973a-3a2fd3e99bfc/imgi-2-761615491-122104351323292474-5126698008421474433-n.jpg"
                alt="Sommerhusrengøring i Norddjurs"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1F1917]/80 via-[#1F1917]/35 to-transparent"></div>

              <div className="relative z-10 flex flex-col items-start">
                <h3 className="text-2xl font-bold font-heading text-white mb-3 drop-shadow-sm">
                  Sommerhusrengøring
                </h3>
                <p className="text-sm text-[#EADFCF] mb-6 line-clamp-3 leading-relaxed drop-shadow-sm">
                  Oplev grundigt sommerhus rengøring med Ses Multi Service, hvor du får aftørret alle flader, støvsuget og vasket gulv. Vi tjekker fejl og mangler, og kan sende billeder, hvis noget skulle være i stykker.
                </p>

                <Link
                  to="/sommerhusrengoering"
                  className="text-white font-bold text-base hover:underline inline-flex items-center gap-2 group/link"
                >
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover/link:translate-x-1" aria-hidden="true"></i>
                </Link>
              </div>
            </article>

            {/* Card 2: Airbnb-rengøring */}
            <article className="relative h-[500px] rounded-3xl overflow-hidden group shadow-sm flex flex-col justify-end p-8 border border-[#EADFCF]">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/161db869-3b85-456e-973a-3a2fd3e99bfc/imgi-21-760620825-10162331829201879-6991498589267761897-n.jpg"
                alt="Airbnb-rengøring i Norddjurs"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1F1917]/80 via-[#1F1917]/35 to-transparent"></div>

              <div className="relative z-10 flex flex-col items-start">
                <h3 className="text-2xl font-bold font-heading text-white mb-3 drop-shadow-sm">
                  Airbnb-rengøring
                </h3>
                <p className="text-sm text-[#EADFCF] mb-6 line-clamp-3 leading-relaxed drop-shadow-sm">
                  Oplev effektiv rengøring med Ses Multi Service! Vi tilbyder grundig rengøring af dit Airbnb, tilpasset dine behov. Vores professionelle team sikrer, at dit hjem altid er rart og indbydende.
                </p>

                <Link
                  to="/airbnb-rengoering"
                  className="text-white font-bold text-base hover:underline inline-flex items-center gap-2 group/link"
                >
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover/link:translate-x-1" aria-hidden="true"></i>
                </Link>
              </div>
            </article>

            {/* Card 3: Rengøring af hus eller lejlighed */}
            <article className="relative h-[500px] rounded-3xl overflow-hidden group shadow-sm flex flex-col justify-end p-8 border border-[#EADFCF]">
              <img
                src={IMAGES.houseApartment}
                alt="Rengøring af hus eller lejlighed i Norddjurs"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1F1917]/80 via-[#1F1917]/35 to-transparent"></div>

              <div className="relative z-10 flex flex-col items-start">
                <h3 className="text-2xl font-bold font-heading text-white mb-3 drop-shadow-sm">
                  Rengøring af hus eller lejlighed
                </h3>
                <p className="text-sm text-[#EADFCF] mb-6 line-clamp-3 leading-relaxed drop-shadow-sm">
                  Nyd et skinnende rent hjem med professionel privat rengøring af helårshuse og lejligheder. Vi tilbyder både faste aftaler og enkeltstående hovedrengøring.
                </p>

                <Link
                  to="/rengoering-af-hus-eller-lejlighed"
                  className="text-white font-bold text-base hover:underline inline-flex items-center gap-2 group/link"
                >
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover/link:translate-x-1" aria-hidden="true"></i>
                </Link>
              </div>
            </article>

            {/* Card 4: Tømning af dødsbo, derefter rengøring */}
            <article className="relative h-[500px] rounded-3xl overflow-hidden group shadow-sm flex flex-col justify-end p-8 border border-[#EADFCF]">
              <img
                src={IMAGES.estateClearance}
                alt="Tømning af dødsbo og efterfølgende rengøring i Norddjurs"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1F1917]/80 via-[#1F1917]/35 to-transparent"></div>

              <div className="relative z-10 flex flex-col items-start">
                <h3 className="text-2xl font-bold font-heading text-white mb-3 drop-shadow-sm">
                  Tømning af dødsbo, derefter rengøring
                </h3>
                <p className="text-sm text-[#EADFCF] mb-6 line-clamp-3 leading-relaxed drop-shadow-sm">
                  Værdig, nænsom og professionel hjælp til rydning og tømning af dødsbo efterfulgt af en komplet, grundig slutrengøring, så boligen er klar til overdragelse eller salg.
                </p>

                <Link
                  to="/toemning-af-doedsbo"
                  className="text-white font-bold text-base hover:underline inline-flex items-center gap-2 group/link"
                >
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover/link:translate-x-1" aria-hidden="true"></i>
                </Link>
              </div>
            </article>

            {/* Card 5: Hunde bad og klip */}
            <article className="relative h-[500px] rounded-3xl overflow-hidden group shadow-sm flex flex-col justify-end p-8 border border-[#EADFCF]">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/161db869-3b85-456e-973a-3a2fd3e99bfc/shutterstock-2574571285.jpg"
                alt="Hunde bad og klip under 10 kg"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1F1917]/80 via-[#1F1917]/35 to-transparent"></div>

              <div className="relative z-10 flex flex-col items-start">
                <h3 className="text-2xl font-bold font-heading text-white mb-3 drop-shadow-sm">
                  Hunde bad og klip
                </h3>
                <p className="text-sm text-[#EADFCF] mb-6 line-clamp-3 leading-relaxed drop-shadow-sm">
                  Hos Ses Multi Service tilbyder jeg klip og vask af din hund under 10 kg. Lav en fast aftale, så din hunds pels bliver velplejet og nem at håndtere. Jeg sikrer en tryg og behagelig oplevelse!
                </p>

                <Link
                  to="/hundeklip-og-vask"
                  className="text-white font-bold text-base hover:underline inline-flex items-center gap-2 group/link"
                >
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover/link:translate-x-1" aria-hidden="true"></i>
                </Link>
              </div>
            </article>

          </div>

        </div>
      </section>

      {/* WHY CHOOSE US SECTION - VERBATIM USER SPEC */}
      <section className="py-16 lg:py-24 bg-[#F4EFE6] border-y border-[#EADFCF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          {/* Header block */}
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <span className="text-xs font-bold uppercase tracking-widest text-[#887055]">
              Fordele & Samarbejde
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-heading text-[#2C2523] tracking-tight">
              Hvorfor Ses Multi Service?
            </h2>
            <p className="text-sm sm:text-base text-[#5C524C] leading-relaxed">
              Vi sætter en ære i gennemsigtighed, høj kvalitet og en nem, fleksibel aftaleproces.
            </p>
          </div>

          {/* 4 Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            
            {/* Card 1: Kvalitet */}
            <div className="bg-[#FFFDF9] rounded-2xl p-7 sm:p-8 border border-[#EADFCF] shadow-xs flex flex-col justify-between hover:shadow-md transition-shadow">
              <div>
                <div className="w-12 h-12 rounded-xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-xl mb-5">
                  <i className="fa-solid fa-sparkles" aria-hidden="true"></i>
                </div>
                <h3 className="text-xl font-bold font-heading text-[#2C2523] mb-3">
                  Kvalitet:
                </h3>
                <p className="text-sm sm:text-base text-[#5C524C] leading-relaxed">
                  Aftaleprocessen med os er både hurtig og nem. Du kan nemt finde den tid, der passer dig bedst, og vi sørger for at imødekomme dine behov. Vi værdsætter din tid og stræber efter at gøre det så enkelt som muligt for dig.
                </p>
              </div>
            </div>

            {/* Card 2: Kontakt */}
            <div className="bg-[#FFFDF9] rounded-2xl p-7 sm:p-8 border border-[#EADFCF] shadow-xs flex flex-col justify-between hover:shadow-md transition-shadow">
              <div>
                <div className="w-12 h-12 rounded-xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-xl mb-5">
                  <i className="fa-solid fa-file-signature" aria-hidden="true"></i>
                </div>
                <h3 className="text-xl font-bold font-heading text-[#2C2523] mb-3">
                  Kontakt:
                </h3>
                <p className="text-sm sm:text-base text-[#5C524C] leading-relaxed">
                  Der oprettes en kontakt for hver kundeaftale, hvor frekvensen for opgavens udførelse, samt den aftalte pris, dokumenteres. Denne procedure sikrer, at begge parter er enige om vilkårene og mindsker risikoen for misforståelser. Det er vigtigt at holde disse oplysninger opdaterede for at sikre en optimal samarbejdsrelation.
                </p>
              </div>
            </div>

            {/* Card 3: Henvendelse */}
            <div className="bg-[#FFFDF9] rounded-2xl p-7 sm:p-8 border border-[#EADFCF] shadow-xs flex flex-col justify-between hover:shadow-md transition-shadow">
              <div>
                <div className="w-12 h-12 rounded-xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-xl mb-5">
                  <i className="fa-solid fa-comments" aria-hidden="true"></i>
                </div>
                <h3 className="text-xl font-bold font-heading text-[#2C2523] mb-3">
                  Henvendelse:
                </h3>
                <p className="text-sm sm:text-base text-[#5C524C] leading-relaxed">
                  Vi er fleksible, hurtige til at besvare henvendelser og effektive i vores arbejde. Vores fokus er altid på at levere et flot resultat, der imødekommer dine behov. Din tilfredshed er vores prioritet, og vi stræber efter at overgå dine forventninger.
                </p>
              </div>
            </div>

            {/* Card 4: Bestilling af flere ydelser */}
            <div className="bg-[#FFFDF9] rounded-2xl p-7 sm:p-8 border border-[#EADFCF] shadow-xs flex flex-col justify-between hover:shadow-md transition-shadow">
              <div>
                <div className="w-12 h-12 rounded-xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-xl mb-5">
                  <i className="fa-solid fa-list-check" aria-hidden="true"></i>
                </div>
                <h3 className="text-xl font-bold font-heading text-[#2C2523] mb-3">
                  Bestilling af flere ydelser
                </h3>
                <p className="text-sm sm:text-base text-[#5C524C] leading-relaxed">
                  En kunde kan bestille flere forskellige ydelser. Bestillingsprocessen foregår typisk ved, at kunden vælger de ønskede ydelser fra vores udvalg, og der bliver udtrykt dette ved det uforpligtende besøg fra Ses Multi Service.  Der bliver lavet kontrakt på hver opgave.
                  <br className="my-2" />
                  Hvis der er spørgsmål omkring bestillingen, kan kunden altid kontakte Ses Multi Service for hjælp.
                </p>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* ABOUT THE OWNER SECTION - HVEM STÅR BAG SES MULTI SERVICE */}
      <section className="py-16 lg:py-24 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="bg-[#FFFDF9] rounded-3xl p-8 sm:p-12 lg:p-14 border border-[#EADFCF] shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              
              {/* Left Column: Heading & Background Story */}
              <div className="lg:col-span-7 space-y-6">
                
                <div>
                  <span className="text-xs font-bold uppercase tracking-widest text-[#887055] block mb-2">
                    Personlig & erfaren service
                  </span>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading text-[#2C2523]">
                    Hvem står bag Ses Multi Service?
                  </h2>
                </div>

                <div className="space-y-4 text-sm sm:text-base text-[#4A403A] leading-relaxed">
                  <p className="font-medium text-[#2C2523] text-base sm:text-lg">
                    Jeg hedder Sofia Emmy Skafsgaard, 62 år, gift.<br />
                    Mor til 4 og har 8 børnebørn
                  </p>
                  
                  <p>
                    Siden 2004-2008 har jeg været uddannet social- og sundhedshjælper og assistent.
                  </p>

                  <p>
                    Jeg har fra 2014 til 2025 haft rengøringsfirmaet Ses Super Ren som startede op i Ørum ved Viborg og ved vores flytning til Vejle / Fredericia i 2017, hvor jeg startede med at have nye kunder der.
                  </p>

                  <p>
                    Jeg tog i sommeren 2024 rengøringsleder uddannelsen på TEC Odense.
                  </p>

                  <p>
                    Ses Super Ren er solgt til Anastasia, som var en af mine medarbejdere, som har kørt det videre i samme ånd.
                  </p>

                  {/* Trustpilot Callout */}
                  <div className="bg-[#FAF7F2] p-5 rounded-2xl border border-[#D8C7B0] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 my-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 text-[#00B67A] font-bold text-sm">
                        <i className="fa-solid fa-star" aria-hidden="true"></i>
                        <span>Trustpilot Anmeldelser</span>
                      </div>
                      <p className="text-xs sm:text-sm text-[#5C524C]">
                        På Trustpilot kan du læse anmeldelser, hvor alt før 1. okt. 2025 handler om den service, jeg har givet kunderne.
                      </p>
                    </div>
                    <a
                      href="https://www.trustpilot.com/review/sessuperren.dk"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#2C2523] hover:bg-black text-white text-xs font-semibold px-4 py-2.5 rounded-xl transition-colors whitespace-nowrap inline-flex items-center gap-2 shrink-0"
                    >
                      <span>Se Trustpilot</span>
                      <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true"></i>
                    </a>
                  </div>

                  <p>
                    Jeg vil gøre mit bedste for at give dit sommerhus en ny friskhed, så du kan have tilfredse kunder som lejer dit sommerhus. Så de kan have fokus på at holde ferie og ikke på om der er gjort godt nok rent.
                  </p>

                  <p>
                    Jeg har haft private / erhvervskunder fra 1 time om ugen til 25 timer om ugen.
                  </p>

                  <p>
                    Jeg arbejdede en del år med sommerhus rengøring i Høll, Hvidbjerg og Mørkholt.
                  </p>

                  <p>
                    Jeg har arbejdet på Skive Soldaterhjem som køkkenassistent. I en periode efter at jeg har solgt firmaet Ses Super Ren.
                  </p>
                </div>

              </div>

              {/* Right Column: "Nu er jeg startet op igen" card & highlights */}
              <div className="lg:col-span-5 space-y-6">
                
                <div className="bg-[#F4EFE6] rounded-2xl p-6 sm:p-8 border border-[#EADFCF] space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#3A302B] text-[#FFFDF9] flex items-center justify-center text-lg shrink-0">
                      <i className="fa-solid fa-arrow-rotate-right" aria-hidden="true"></i>
                    </div>
                    <h3 className="text-xl font-bold font-heading text-[#2C2523]">
                      Nu er jeg startet op igen:
                    </h3>
                  </div>

                  <ul className="space-y-3.5 text-xs sm:text-sm text-[#4A403A]">
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                      <span>Savnede kundekontakten og fandt ud af at der er et behov for den hjælp som jeg kan tilbyde med rengøring af sommerhuse og hundeklip.</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                      <span>Jeg udfører alle opgaverne som det er nu men håber på at få så travlt at jeg kan ansætte nogle nye medarbejdere.</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-location-dot text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                      <span><strong>Dækningsområde:</strong> Jeg kører primært i Ørsted, Allingåbro og Fjellerup området.</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                      <span>Jeg har alt udstyret med til rengøring af dit sommerhus, Jeg ser frem til at komme forbi dit sommerhus med et uforpligtende tilbud.</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-paw text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                      <span>Hundebad og klip foregår på vores private adr.</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                      <span>Ind imellem har jeg også et par udekørende vagter i hjemmeplejen Allingåbro.</span>
                    </li>
                  </ul>

                  <div className="pt-4 border-t border-[#D8C7B0] text-center sm:text-left space-y-3">
                    <p className="font-semibold text-sm text-[#2C2523]">
                      Jeg ser frem til at høre fra dig i forhold til Rengøringsopgaven eller hundeklip
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2.5">
                      <a
                        href={`tel:${BUSINESS_INFO.phone}`}
                        className="bg-[#3A302B] hover:bg-[#231C19] text-white text-xs font-semibold px-4 py-2.5 rounded-xl transition-colors inline-flex items-center justify-center gap-2"
                      >
                        <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                        <span>Ring på {BUSINESS_INFO.phoneFormatted}</span>
                      </a>
                      <Link
                        to="/kontakt"
                        className="bg-[#EADFCF] hover:bg-[#D8C7B0] text-[#2C2523] text-xs font-semibold px-4 py-2.5 rounded-xl transition-colors inline-flex items-center justify-center gap-2 border border-[#D8C7B0]"
                      >
                        <i className="fa-solid fa-paper-plane text-xs text-[#887055]" aria-hidden="true"></i>
                        <span>Send forespørgsel</span>
                      </Link>
                    </div>
                  </div>

                </div>

                {/* Qualification badge summary */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="bg-[#FFFDF9] p-3.5 rounded-xl border border-[#EADFCF] flex items-center gap-2.5">
                    <i className="fa-solid fa-graduation-cap text-base text-[#887055]" aria-hidden="true"></i>
                    <div>
                      <p className="font-bold text-[#2C2523]">TEC Odense</p>
                      <p className="text-[#6B5640] text-[11px]">Rengøringsleder 2024</p>
                    </div>
                  </div>
                  <div className="bg-[#FFFDF9] p-3.5 rounded-xl border border-[#EADFCF] flex items-center gap-2.5">
                    <i className="fa-solid fa-award text-base text-[#887055]" aria-hidden="true"></i>
                    <div>
                      <p className="font-bold text-[#2C2523]">Over 10 års erfaring</p>
                      <p className="text-[#6B5640] text-[11px]">Ses Super Ren (2014-2025)</p>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </div>

        </div>
      </section>

      {/* SERVICE AREA SECTION - NORDDJURS */}
      <section className="py-14 bg-[#2C2523] text-[#EADFCF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-3">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#C2AB8E] uppercase tracking-wider">
                <i className="fa-solid fa-map-location-dot" aria-hidden="true"></i>
                <span>Dækningsområde</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold font-heading text-[#FFFDF9]">
                Rengøring af sommerhuse i hele Norddjurs
              </h2>
              <p className="text-sm text-[#C2AB8E] leading-relaxed">
                Baseret i Ørsted (8950) kører vi ud til sommerhuse og ferieboliger i hele Norddjurs, herunder Fjellerup Strand, Bønnerup Strand, Auning, Allingåbro, Vivild, Udbyhøj og omegn.
              </p>
            </div>

            <div className="lg:col-span-5 bg-[#3A302B] rounded-2xl p-6 border border-[#52453E] space-y-3">
              <h3 className="text-base font-bold text-[#FFFDF9]">Har du spørgsmål til området?</h3>
              <p className="text-xs text-[#C2AB8E]">
                Ring direkte til Ses Multi Service for en snak om din adresse og sæsonaftaler.
              </p>
              <div className="pt-1">
                <a
                  href={`tel:${BUSINESS_INFO.phone}`}
                  className="inline-flex items-center gap-2 bg-[#FAF7F2] text-[#2C2523] px-5 py-2.5 rounded-xl text-xs font-semibold hover:bg-white transition-colors whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true"></i>
                  <span className="whitespace-nowrap">Ring på {BUSINESS_INFO.phoneFormatted}</span>
                </a>
              </div>
            </div>
          </div>

          {/* Cleaning Video Container directly under Dækningsområde grid */}
          <div className="pt-2">
            <CleaningVideo />
          </div>
        </div>
      </section>
    </>
  );
}
